import apiClient from './api';
import { t } from 'i18n';

/**
 * R5 Zaynic Coins + verified reviews -- every call returns
 * { success: true, data } or { success: false, error } (never throws).
 * The backend's own `detail` string is preferred (it is already specific);
 * FastAPI's 422 error arrays fall back to a plain message.
 */
async function call(promise, fallbackKey) {
  try {
    const response = await promise;
    return { success: true, data: response.data };
  } catch (error) {
    const detail = error.response?.data?.detail;
    return {
      success: false,
      status: error.response?.status,
      error: typeof detail === 'string' ? detail : t(fallbackKey),
    };
  }
}

// Public config is the same for everyone and rarely changes -- one fetch per page load.
let configPromise = null;

export const LoyaltyService = {
  getConfig() {
    if (!configPromise) {
      configPromise = call(apiClient.get('/loyalty/config'), 'coins.errors.load').then((res) => {
        if (!res.success) configPromise = null;
        return res;
      });
    }
    return configPromise;
  },
  getMine: () => call(apiClient.get('/loyalty/me'), 'coins.errors.load'),
  getHistory: (limit = 200) => call(apiClient.get('/loyalty/history', { params: { limit } }), 'coins.errors.load'),
};

/** Rough "Earn ~N coins" for a product page (base Silver rate; the real
 * amount is decided at delivery from the tier and any coin discount). */
export function estimateCoins(config, amount) {
  if (!config || !config.enabled) return 0;
  const per = Number(config.earn_per_rupees) || 0;
  if (per <= 0 || !(Number(amount) > 0)) return 0;
  return Math.floor(Number(amount) / per);
}

export const ReviewMediaService = {
  upload(kind, file) {
    const form = new FormData();
    form.append('kind', kind);
    form.append('file', file);
    return call(apiClient.post('/reviews/media', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
      'reviews.errors.upload');
  },
  report: (reviewId, reason) => call(apiClient.post(`/reviews/${reviewId}/report`, { reason: reason || null }),
    'reviews.errors.report'),
  mine: () => call(apiClient.get('/reviews/mine'), 'reviews.errors.load'),
  reviewable: () => call(apiClient.get('/reviews/reviewable'), 'reviews.errors.load'),
};

export const VendorReviewsService = {
  list: () => call(apiClient.get('/vendor/reviews'), 'reviews.errors.load'),
  reply: (reviewId, reply) => call(apiClient.post(`/vendor/reviews/${reviewId}/reply`, { reply }),
    'reviews.errors.reply'),
  credits: () => call(apiClient.get('/vendor/billing/credits'), 'coins.errors.load'),
};

export const AdminReviewsService = {
  list: (view = 'pending') => call(apiClient.get('/admin/reviews', { params: { view, limit: 100 } }),
    'reviews.errors.load'),
  approve: (id) => call(apiClient.post(`/admin/reviews/${id}/approve`), 'reviews.errors.moderate'),
  reject: (id, reason) => call(apiClient.post(`/admin/reviews/${id}/reject`, { reason }), 'reviews.errors.moderate'),
};

export const AdminLoyaltyService = {
  getSettings: () => call(apiClient.get('/admin/loyalty/settings'), 'coins.errors.load'),
  saveSettings: (values) => call(apiClient.put('/admin/loyalty/settings', { values }), 'coins.errors.save'),
  searchUsers: (q) => call(apiClient.get('/admin/loyalty/users', { params: { q } }), 'coins.errors.load'),
  ledger: (userId) => call(apiClient.get(`/admin/loyalty/users/${userId}/ledger`), 'coins.errors.load'),
  adjust: (userId, amount, reason) => call(apiClient.post(`/admin/loyalty/users/${userId}/adjust`, { amount, reason }),
    'coins.errors.save'),
  freeze: (userId, frozen, reason) => call(apiClient.post(`/admin/loyalty/users/${userId}/freeze`, { frozen, reason }),
    'coins.errors.save'),
  payables: () => call(apiClient.get('/admin/loyalty/payables'), 'coins.errors.load'),
  runSweep: () => call(apiClient.post('/admin/loyalty/run-sweep'), 'coins.errors.save'),
};
